import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface Hotel {
  id: string;
  name: string;
  type: string;
  address: string;
  city: string;
  rating: number;
  reviewCount: number;
  pricePerNight: number;
  totalPrice: number;
  currency: string;
  amenities: string[];
  images: string[];
  cancellationPolicy: string;
  location: {
    lat: number;
    lng: number;
  };
}

interface UseHotelSearchReturn {
  results: Hotel[];
  search: (params: {
    cityCode: string;
    checkInDate: string;
    checkOutDate: string;
    adults?: number;
    roomQuantity?: number;
    priceRange?: string;
    ratings?: string[];
    amenities?: string[];
  }) => Promise<void>;
  loading: boolean;
  error: string | null;
  demoMode: boolean;
  clearResults: () => void;
  clearError: () => void;
}

export function useHotelSearch(): UseHotelSearchReturn {
  const [results, setResults] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demoMode, setDemoMode] = useState(false);

  const search = useCallback(async (params: {
    cityCode: string;
    checkInDate: string;
    checkOutDate: string;
    adults?: number;
    roomQuantity?: number;
    priceRange?: string;
    ratings?: string[];
    amenities?: string[];
  }): Promise<void> => {
    setLoading(true);
    setError(null);
    setDemoMode(false);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/search-hotels`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify(params),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Hotels API error:', response.status, errorText);
        throw new Error(errorText || `Failed to search hotels (HTTP ${response.status})`);
      }

      const data = await response.json();
      console.log('Hotels API response:', data);

      if (data.error) {
        console.error('Hotels API error message:', data.error);
        setError(data.message || data.error);
        setResults([]);
        return;
      }

      const hotels = data.hotels || [];
      setResults(hotels);
      setDemoMode(data.demoMode || false);

      if (hotels.length === 0) {
        if (!data.success) {
          setError(data.message || 'No hotels found. Try different dates or location.');
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to search hotels';
      console.error('Hotels search error:', err);
      setError(message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    results,
    search,
    loading,
    error,
    demoMode,
    clearResults,
    clearError,
  };
}
